import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  User, 
  LogOut, 
  Home, 
  Calendar,
  Building,
  DollarSign,
  Users,
  BarChart3,
  Menu,
  Star
} from 'lucide-react';
import { becomeHost } from '../lib/api';
import { toast } from 'sonner';
import logo from '../assets/logo.png';

export default function Navbar() {
  const { user, isAuthenticated, isHost, isAdmin, logout, refreshUser } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleBecomeHost = async () => {
    try {
      const response = await becomeHost();
      toast.success(response.data.message);
      // Refresh user data to update role
      await refreshUser();
      navigate('/app/host');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upgrade to host');
    }
  };

  return (
    <nav className="glass-nav sticky top-0 z-50">
      <div className="container-main">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <img src={logo} alt="Southwest FL Vacation Rentals" className="h-14 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link 
              to="/search" 
              className="text-slate-600 hover:text-primary transition-colors font-medium"
              data-testid="nav-search"
            >
              Browse Stays
            </Link>
            <Link 
              to="/host" 
              className="text-slate-600 hover:text-primary transition-colors font-medium"
              data-testid="nav-host"
            >
              List Your Property
            </Link>
          </div>

          {/* Auth Section */}
          <div className="flex items-center gap-4">
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="flex items-center gap-2 rounded-full"
                    data-testid="user-menu-trigger"
                  >
                    <Menu className="w-4 h-4" />
                    <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-medium">
                      {user?.name?.charAt(0).toUpperCase()}
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  
                  <DropdownMenuItem onClick={() => navigate('/app')} data-testid="menu-dashboard">
                    <Home className="w-4 h-4 mr-2" />
                    Dashboard
                  </DropdownMenuItem>
                  
                  <DropdownMenuItem onClick={() => navigate('/app/bookings')} data-testid="menu-bookings">
                    <Calendar className="w-4 h-4 mr-2" />
                    My Bookings
                  </DropdownMenuItem>
                  
                  {/* Show "Become a Host" for guests who aren't hosts yet */}
                  {!isHost && !isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        onClick={handleBecomeHost} 
                        data-testid="menu-become-host"
                        className="text-primary font-medium"
                      >
                        <Star className="w-4 h-4 mr-2" />
                        Become a Host
                      </DropdownMenuItem>
                    </>
                  )}
                  
                  {isHost && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/app/host')} data-testid="menu-host-dashboard">
                        <Building className="w-4 h-4 mr-2" />
                        Host Dashboard
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/app/host/properties')} data-testid="menu-properties">
                        <Home className="w-4 h-4 mr-2" />
                        My Properties
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/app/host/reservations')} data-testid="menu-reservations">
                        <Calendar className="w-4 h-4 mr-2" />
                        Reservations
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/app/host/payouts')} data-testid="menu-payouts">
                        <DollarSign className="w-4 h-4 mr-2" />
                        Payouts
                      </DropdownMenuItem>
                    </>
                  )}
                  
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/app/admin')} data-testid="menu-admin">
                        <BarChart3 className="w-4 h-4 mr-2" />
                        Admin Dashboard
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/app/admin/users')} data-testid="menu-admin-users">
                        <Users className="w-4 h-4 mr-2" />
                        Manage Users
                      </DropdownMenuItem>
                    </>
                  )}
                  
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    Log Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/login')}
                  data-testid="nav-login"
                >
                  Log In
                </Button>
                <Button 
                  className="btn-primary"
                  onClick={() => navigate('/register')}
                  data-testid="nav-register"
                >
                  Sign Up
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
